import {
  AfterViewInit,
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Injector,
  TemplateRef,
  ViewChild,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { pairwise, startWith } from 'rxjs';
import { AddedLink, LinkedRequirement, TestCaseLinkType } from '../../../models/test-case-link.model';
import { Requirement } from '../../../models/requirement.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { LINK_TYPE_META, LinkTypeMeta } from '../../../shared/link-type-meta';
import { IconComponent } from '../../../components/icon/icon.component';
import { AnforderungAuswahlDialogComponent } from '../../../components/anforderung-auswahl-dialog/anforderung-auswahl-dialog.component';

// Backend-@Size: reference ist auf 1024 Zeichen begrenzt.
const REFERENCE_MAX = 1024;

const LINK_TYPES: readonly TestCaseLinkType[] = ['REQUIREMENT', 'JIRA', 'GITHUB', 'TESTCASE', 'URL'];

interface ChosenRequirement extends LinkedRequirement {
  key: string;
}

function urlSchemeValidator(group: AbstractControl): ValidationErrors | null {
  const type = group.get('type')?.value as TestCaseLinkType;
  const reference = (group.get('reference')?.value as string) ?? '';
  if (type !== 'URL') {
    return null;
  }
  const normalized = reference.trim().toLowerCase();
  if (normalized && !normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    return { urlScheme: true };
  }
  return null;
}

@Component({
  selector: 'app-add-link-modal',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    Modal,
    ModalEscapeGuardDirective,
    MatIcon,
    IconComponent,
    AnforderungAuswahlDialogComponent,
  ],
  templateUrl: './add-link-modal.component.html',
  styleUrl: './add-link-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddLinkModalComponent implements AfterViewInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  readonly projectId = input.required<string>();
  readonly visible = model(false);
  readonly excludedRequirementKeys = input<readonly string[]>([]);
  readonly added = output<AddedLink>();

  readonly linkTypes = LINK_TYPES;
  readonly fieldLengths = FIELD_LENGTHS;
  readonly referenceMax = REFERENCE_MAX;

  readonly form = this.fb.group(
    {
      type: this.fb.control<TestCaseLinkType>('REQUIREMENT', Validators.required),
      reference: ['', [Validators.required, Validators.maxLength(REFERENCE_MAX)]],
      displayLabel: ['', Validators.maxLength(FIELD_LENGTHS.NAME_MAX)],
    },
    { validators: urlSchemeValidator },
  );

  readonly linkType = signal<TestCaseLinkType>('REQUIREMENT');
  readonly chosenRequirement = signal<ChosenRequirement | null>(null);
  readonly pickerOpen = signal(false);
  private pickerTrigger: HTMLElement | null = null;

  @ViewChild('content') contentTemplate!: TemplateRef<unknown>;
  resolvedTemplate: TemplateRef<unknown> | null = null;

  constructor() {
    const typeControl = this.form.controls.type;
    typeControl.valueChanges
      .pipe(startWith(typeControl.value), pairwise(), takeUntilDestroyed())
      .subscribe(([previous, next]) => this.onTypeChanged(previous, next));
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.contentTemplate;
    this.cdr.detectChanges();
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    if (this.visible()) {
      this.onCancel();
    }
  }

  hasUrlSchemeError(): boolean {
    return this.form.hasError('urlScheme');
  }

  referenceErrorKey(): string | null {
    const control = this.form.controls.reference;
    if (control.touched && control.hasError('required')) {
      return this.linkType() === 'REQUIREMENT'
        ? 'testCases.form.links.modal.requirementRequired'
        : 'testCases.form.links.modal.referenceRequired';
    }
    if (this.hasUrlSchemeError()) {
      return 'testCases.form.links.modal.urlInvalid';
    }
    return null;
  }

  typeMeta(type: TestCaseLinkType): LinkTypeMeta {
    return LINK_TYPE_META[type];
  }

  typeCardTestId(type: TestCaseLinkType): string {
    return `tf-verknuepfung-typ-${type}`;
  }

  requirementRoute(requirement: ChosenRequirement): string[] {
    return ['/projects', this.projectId(), 'requirements', requirement.id, 'edit'];
  }

  onTypeCardClick(type: TestCaseLinkType, event: Event): void {
    this.form.controls.type.setValue(type);
    if (type === 'REQUIREMENT') {
      this.openPicker(event.currentTarget as HTMLElement);
    }
  }

  onTypeCardKeydown(event: KeyboardEvent): void {
    const delta = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
    if (!delta) {
      return;
    }
    event.preventDefault();
    const len = this.linkTypes.length;
    const next = this.linkTypes[(this.linkTypes.indexOf(this.linkType()) + delta + len) % len];
    this.form.controls.type.setValue(next);
    this.cdr.detectChanges();
    this.host.nativeElement.querySelector<HTMLElement>(`[data-testid="${this.typeCardTestId(next)}"]`)?.focus();
  }

  openPicker(trigger: HTMLElement | null): void {
    this.pickerTrigger = trigger;
    this.pickerOpen.set(true);
  }

  onRequirementPicked(requirement: Requirement): void {
    this.chosenRequirement.set({
      key: requirement.key ?? '',
      id: requirement.id,
      title: requirement.title,
      type: requirement.type,
      status: requirement.status,
      sourceType: requirement.sourceType,
    });
    this.form.controls.reference.setValue(requirement.key ?? '');
    this.closePicker();
  }

  closePicker(): void {
    this.pickerOpen.set(false);
    this.form.controls.reference.markAsTouched();
    const trigger = this.pickerTrigger;
    this.pickerTrigger = null;
    afterNextRender(
      () => {
        const target = trigger?.isConnected
          ? trigger
          : this.host.nativeElement.querySelector<HTMLElement>('.add-link__anf-aendern');
        target?.focus();
      },
      { injector: this.injector },
    );
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const label = raw.displayLabel.trim();
    const chosen = raw.type === 'REQUIREMENT' ? this.chosenRequirement() : null;
    this.added.emit({
      request: {
        type: raw.type,
        reference: raw.reference.trim(),
        displayLabel: raw.type === 'REQUIREMENT' ? null : label || null,
      },
      ...(chosen ? { requirement: this.linkedRequirement(chosen) } : {}),
    });
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  private linkedRequirement({ id, title, type, status, sourceType }: ChosenRequirement): LinkedRequirement {
    return { id, title, type, status, sourceType };
  }

  private onTypeChanged(previous: TestCaseLinkType, next: TestCaseLinkType): void {
    this.linkType.set(next);
    if (previous !== next && (previous === 'REQUIREMENT' || next === 'REQUIREMENT')) {
      this.form.controls.reference.reset('');
      this.chosenRequirement.set(null);
    }
  }

  private close(): void {
    this.visible.set(false);
    this.pickerOpen.set(false);
    this.pickerTrigger = null;
    this.form.reset({ type: 'REQUIREMENT', reference: '', displayLabel: '' });
    this.chosenRequirement.set(null);
  }
}
